import { Button } from '@/components/_ui/Button'
import T, { useTranslate } from '@/components/Helpers/Translate'
import { useModals } from '@/components/ModalManager/useModals'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { hydra } from '@/lib/services/Hydra'
import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import type { HydraGetSubscriptionItem } from '@/lib/services/Hydra/account/GET/getSubscriptions'
import type {
  EditTransactions,
  HydraEditSubscriptionItem,
} from '@/lib/services/Hydra/account/POST/editSubscription'
import type { QuotesCallType } from '@/lib/services/Hydra/quotesCallType'
import {
  TrackSubscriptionError,
  TrackAddSubscriptionCompleted,
} from '@/lib/services/mixpanel/events/subscriptionEvents'
import { getSubcriptionEditUrl } from '@/lib/utils/shared/getSubscriptionEditUrl'
import type { CartItemType } from '@/store/types/cart'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useState } from 'react'

export const useAddNewItemToSubscription = () => {
  const {
    user: { token, customerType },
    app: { marketExceptions },
    addToCart,
    setApp,
    setAppAddProductToSubscription,
  } = useGlobalStore()

  const { modals } = useModals()
  const { translate } = useTranslate()
  const { clientRedirect } = useClientRedirect()

  const [
    isLoadingAddingItemToSubscription,
    setIsLoadingAddingItemToSubscription,
  ] = useState(false)

  const handleOpenErrorUpdatingSubscriptionModal = ({
    itemToAddToSubscription,
  }: {
    itemToAddToSubscription: CartItemType
  }) => {
    modals.openModal({
      type: 'confirm',
      modalId: 'error-updating-subscription',
      modal: {
        title: translate('add_to_cart_product_error'),
        confirmText: translate('add_to_cart'),
        cancelText: translate('cancel'),
        onConfirm: () => {
          addToCart(itemToAddToSubscription)
          setApp({
            isCartOpen: true,
          })

          modals.closeModals()
        },
        description: (
          <div>
            <T>we_were_unable_to_update_your_subscription</T>
          </div>
        ),
      },
    })
  }

  const handleOpenSuccessUpdatingSubscriptionModal = () => {
    modals.openModal({
      type: 'modal',
      modalId: 'subscription-updated-successfully',
      modal: {
        children: (
          <div className="text-center">
            <div className="my-5 flex items-center justify-center">
              <Image
                height={50}
                width={50}
                src="/icons/checked-circle.svg"
                alt="Check"
              />
            </div>

            <h3 className="text-lg font-bold">
              <T>items_successfully_added_to_subscription</T>
            </h3>
            <T>items_will_not_show_in_your_cart</T>
            <div className="mt-6 flex items-center justify-center">
              <Button
                variant="secondary"
                onClick={async () => {
                  modals.closeModals()

                  await clientRedirect.to({
                    path: '/',
                    withPersistingLocale: true,
                  })
                }}
              >
                <T>continue_shopping</T>
              </Button>
            </div>
          </div>
        ),
      },
    })
  }

  const handleAddItemToSubscription = ({
    subscription,
    itemToAddToSubscription,
    orderCalculation,
  }: {
    subscription: HydraGetSubscriptionItem
    itemToAddToSubscription: CartItemType
    orderCalculation: (QuotesCallType & HydraErrorType) | null
  }) => {
    if (!token || !subscription || !itemToAddToSubscription) return
    if (
      !subscription.transactions.items ||
      !subscription.transactions.items[0].methodDetails
    ) {
      return
    }

    if (
      !orderCalculation ||
      orderCalculation.error ||
      !orderCalculation.items
    ) {
      return
    }

    const subscriptionOrderURL = getSubcriptionEditUrl(subscription)

    const orderItem = orderCalculation.items[0]
    const newPaymentSelection: EditTransactions = {
      items: [
        {
          ...subscription.transactions.items[0],
          amount: 'this.terms.total',
        },
      ],
    }

    const requestData: HydraEditSubscriptionItem = {
      customer: subscription.customer,
      transactions: newPaymentSelection,
      recurrence: {
        schedule: {
          dayOfMonth: subscription.recurrence.schedule.dayOfMonth.toString(),
          month: subscription.recurrence.schedule.month,
          dateStarts: subscription.recurrence.dateStarts || '',
        },
        status: 'Enabled',
      },
      lines: {
        items: orderItem.lines.items.map((line) => ({
          item: {
            href: line.item.href,
            id: line.item.id,
          },
          quantity: line.quantity,
        })),
      },
      shippingMethod: subscription.shippingMethod,
      shipToAddress: subscription.shipToAddress,
      shipToEmail: subscription.shipToEmail,
      shipToName: subscription.shipToName,
      shipToPhone: subscription.shipToPhone,
    }

    ;(async () => {
      setIsLoadingAddingItemToSubscription(true)

      const editSubscriptionResponse = await hydra.editSubscription({
        hydraToken: token,
        url: subscriptionOrderURL,
        data: requestData,
      })

      if (!editSubscriptionResponse || editSubscriptionResponse.error) {
        setAppAddProductToSubscription({
          isConfirmAddToActiveSubscriptionModalOpen: false,
        })
        handleOpenErrorUpdatingSubscriptionModal({ itemToAddToSubscription })
        setIsLoadingAddingItemToSubscription(false)

        TrackSubscriptionError({
          subscriptionId: subscription.id.unicity,
          responseCode:
            editSubscriptionResponse?.error?.error_message?.toString(),
          errorMessage: editSubscriptionResponse?.error?.code?.toString(),
        }).catch(() => {})

        return
      }

      handleOpenSuccessUpdatingSubscriptionModal()

      TrackAddSubscriptionCompleted({
        scenario: 'combine_subscription',
        action: 'add',
        subscriptionId: subscription.id.unicity,
        cartItem: itemToAddToSubscription,
        itemCount: itemToAddToSubscription.qty,
        customerType,
        currencyCode: marketExceptions?.currencyCode || 'USD',
      }).catch(() => {})

      setAppAddProductToSubscription({
        isConfirmAddToActiveSubscriptionModalOpen: false,
      })

      setIsLoadingAddingItemToSubscription(false)
    })().catch(() => {
      setAppAddProductToSubscription({
        isConfirmAddToActiveSubscriptionModalOpen: false,
      })
      handleOpenErrorUpdatingSubscriptionModal({ itemToAddToSubscription })
      setIsLoadingAddingItemToSubscription(false)
    })
  }

  return {
    handleAddItemToSubscription,
    isLoadingAddingItemToSubscription,
  }
}
